import Vuex from 'vuex'
import axios, {AxiosRequestConfig} from "axios"
import createPersistedState from "vuex-persistedstate"
import {Locales} from "@/i18n/locales"
import {i18n} from "@/main"
import {LichenRecordField, newRecord, newSite} from "@/config/config-lichen"
import {updateRecordURL, saveRecordURL, getRecordByIdURL, deleteRecordURL} from "@/config/app-settings"
import router from "@/router";
import {toUrlParams} from "@/store/data-transformer";
import {ComponentEnum} from "@/config/config-types";

export type User = {
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    street: string,
    streetNo: string,
    zip: string,
    city: string,
    country: number,
    certificates: number[],
    phoneNo: string,
    selfEstimation: number
    imageUploadPermission: number
    restrPublication: number
    userNameRight: number
    personId: number
}

const emptyLichenRecordForm = {
    site: [] as LichenRecordField[],
    records: [] as LichenRecordField[][],
}

const state = {
    token: '',
    tokenTimestamp: null as number | null,
    user: {} as User,
    emailChanged: false as Boolean,
    language: Locales.DE,
    selectedRecordHeaders: [],
    selectedValidationRecordHeaders: [],
    lichenRecordForm: Object.assign({}, emptyLichenRecordForm),
    formState: 'new' as 'new' | 'editing' | 'editingUnsaved',
    notification: '' as string,
    menubarClicked: false as Boolean,
    errors: {}
}

const persistedState = createPersistedState({
    paths: ['token', 'tokenTimestamp', 'user', 'language', 'selectedRecordHeaders', 'selectedValidationRecordHeaders']
})

export const clearLichenForm = function (formField: LichenRecordField[]) {
    formField.forEach((value) => {
        // special handling on non null config values
        if (["taxonReferenceSuggested"].includes(value.name)) {
            // @ts-ignore
            value.value.forEach((val: LichenRecordField) => {
                val.value = null
            })
        } else if (["images", "specimens", "coords"].includes(value.name)) {
            value.value = []
        } else {
            value.value = null
        }
    });
    return formField

}

export default new Vuex.Store({
    state,
    getters: {
        loggedIn: state => {
            return state.token.length > 0
        },
        personId: state => {
            return state.user.personId
        },
        fullName: state => {
            return state.user.firstName + ' ' + state.user.lastName
        },
        formState: state => {
            return state.formState
        },
        siteIsValid: state => {
            return state.lichenRecordForm.site.every(field => !field.required || (field.required && field.value !== null && field.value !== ''))
        },
        recordsAreValid: state => {
            return state.lichenRecordForm.records.every(lichenRecord => lichenRecord.every(field => !field.required || (field.required && field.value !== null)))
        }
    },
    mutations: {
        ['setLang'](state, payload: Locales) {
            i18n.global.locale = payload
            state.language = payload
        },
        ['setToken'](state, payload: string) {
            // set timestamp for session time out an logging out user when token expires
            state.token = payload;
            state.tokenTimestamp = Date.now()
        },
        ['setSelectedRecordHeaders'](state, payload: any) {
            state.selectedRecordHeaders = payload;
        },
        ['setSelectedValidationRecordHeaders'](state, payload: any) {
            state.selectedValidationRecordHeaders = payload;
        },
        ['setUser'](state, payload) {
            state.user = payload
        },
        ['setEmailChanged'](state, payload) {
            state.emailChanged = payload
        },
        ['setFormState'](state, payload) {
            state.formState = payload
        },
        ['setLichenRecordForm'](state, payload) {
            Object.assign(state.lichenRecordForm, payload)
        },
        ['addLichenRecord'](state, payload: LichenRecordField[]) {
            state.lichenRecordForm.records.push(clearLichenForm(payload))
        },
        ['removeLichenRecord'](state, idx: number) {
            state.lichenRecordForm.records.splice(idx, 1)
        },
        ['finalDeletionOfLichenRecord'](state, recordId: number) {
            axios.delete(deleteRecordURL + '?recordId=' + recordId).then((res) => {
                    console.log("response ", res)
                }
            ).catch((e) => {
                console.log("error ", e)
            })
        },
        ['setLichenRecordValue'](state, payload: { idx: number, key: string, value: any }) {
            const keys = payload.key.split('.')
            const lichenRecord = state.lichenRecordForm.records[payload.idx]
            let field = lichenRecord.find(field => field.name === keys[0])
            if (keys.length === 2 && field !== undefined && field !== null) {
                // @ts-ignore
                field = field.value.find(f => f.name === keys[1])
            }
            // @ts-ignore
            field.value = payload.value
        },
        ['setSiteValue'](state, payload: { key: string, value: any }) {
            const keys = payload.key.split('.')
            let field = state.lichenRecordForm.site.find(field => field.name === keys[0])
            if (keys.length === 2 && field !== undefined && field !== null) {
                // @ts-ignore
                field = field.value.find(f => f.name === keys[1])
            }
            // @ts-ignore
            field.value = payload.value
        },
        ['setNotification'](state, msg: string) {
            state.notification = msg
        },
    },
    actions: {
        ['submitLichenRecordForm']({state, dispatch}) {
            const data = toUrlParams(state.lichenRecordForm)
            const config: AxiosRequestConfig = {
                method: state.formState == 'editing' ? 'put' : 'post',
                url: state.formState == 'editing' ? updateRecordURL : saveRecordURL,
                headers: {
                    'Content-Type': 'application/json'
                },
                data: JSON.stringify(data)
            }
            console.log("submitLichenRecordForm - ", data)
            return axios(config)
                .then(() => {
                    dispatch('deleteLichenRecordForm')
                    state.formState = 'new'
                    return 'Fundmeldung wurde erfolgreich übermittelt! Besten Dank!'
                })
        },
        ['edit']({commit}, payload: any) {
            state.formState = 'editing'

            const lichenRecordForm = {
                site: JSON.parse(JSON.stringify(newSite())),
                records: [JSON.parse(JSON.stringify(newRecord()))]
            }
            axios.get(getRecordByIdURL + '?recordId=' + payload)
                .then(res => {
                    console.log("BACKEND result: ", res)
                    const result = res.data
                    result.coords = [result.x, result.y]
                    lichenRecordForm.site.forEach((field: LichenRecordField) => {
                        if (field.name === 'siteId') {
                            field.value = result.siteId
                        } else if (result[field.name]) {
                            field.value = result[field.name]
                        } else {
                            console.log(">>null field value", field.name, res.data)
                        }
                    })
                    res.data.records.forEach((savedLichenRecord: any, i: number) => {
                        const newLichenRecord = newRecord()
                        newLichenRecord.forEach((field: LichenRecordField) => {
                            if (savedLichenRecord[field.name]) {
                                if (field.name === 'recordId') {
                                    field.value = savedLichenRecord.recordId
                                } else if (field.name === 'taxonReferenceSuggested') {
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'ifFlorAppReferenceWork').value = savedLichenRecord[field.name].ifFlorAppReferenceWork
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'taxonId').value = savedLichenRecord[field.name].taxonId
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonName').value = savedLichenRecord[field.name].suggestedTaxonName
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonPublication').value = savedLichenRecord[field.name].suggestedTaxonPublication
                                    // @ts-ignore
                                    // field.value.find(f => f.name === 'suggestedTaxonDescription').value = savedLichenRecord[field.name].suggestedTaxonDescription
                                    // @ts-ignore
                                    field.value.find(f => f.name === 'suggestedTaxonComment').value = savedLichenRecord[field.name].suggestedTaxonComment
                                } else {
                                    // @ts-ignore
                                    field.value = savedLichenRecord[field.name]
                                }
                            } else {
                                console.log(">>", field.name, savedLichenRecord[field.name])
                            }
                        })
                        lichenRecordForm.records[i] = newLichenRecord
                    })
                    commit('setLichenRecordForm', lichenRecordForm)
                    router.push({name: 'Form'})
                })
        },
        ['addLichenRecord']({commit}) {
            commit('addLichenRecord', newRecord())
        },
        ['newLichenRecord']({commit}) {
            const payload = {site: newSite(), records: [newRecord(),]}
            commit('setLichenRecordForm', payload)
        },
        ['deleteLichenRecordForm']({commit}) {
            commit('setLichenRecordForm', emptyLichenRecordForm)
        },
        ['login']({commit}, payload: { token: string; user: any }) {
            commit('setToken', payload.token)
            axios.defaults.headers.common['Authorization'] = 'Bearer ' + payload.token
            commit('setUser', payload.user)
        },
        ['logout']({commit}) {
            commit('setToken', '')
            state.tokenTimestamp = 0
            axios.defaults.headers.common['Authorization'] = ''
        },
        ['notify']({commit}, msg: string) {
            commit('setNotification', msg)
            setTimeout(() => commit('setNotification', ''), 5000)
        },
        ['validate']({state}, errors: {}) {
            state.errors = errors
        }
    },
    plugins: [persistedState]
})